/**
 * 📊 EXPORTAÇÃO DE RELATÓRIOS - Gains Monitor
 * Exporta dados do dashboard para Excel e PDF
 */

import type { KaizenRecord, GainsDashboardData, MetricsSnapshot } from '../types/gainsMonitor';
import { GainsMonitorService } from './gainsMonitorService';

function measuredLeadReduction(kaizen: KaizenRecord): number | null {
  if (kaizen.baseline_lead_time == null || kaizen.current_lead_time == null) return null;
  if (kaizen.baseline_lead_time <= 0) return null;
  return kaizen.baseline_lead_time - kaizen.current_lead_time;
}

function metricText(value: number | null | undefined, digits = 2): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return value.toFixed(digits);
}

/**
 * 📊 Exporta relatório completo para Excel
 */
export async function exportGainsToExcel(
  kaizens: KaizenRecord[],
  dashboardData: GainsDashboardData,
  organizationName: string = 'Organização'
): Promise<void> {
  try {
    const XLSX = await import('xlsx');
    const { utils, writeFile } = XLSX;

    // ========================================================================
    // SHEET 1: Dashboard Summary
    // ========================================================================
    const summaryData = [
      { Métrica: 'Total de Kaizens', Valor: dashboardData.total_kaizens },
      { Métrica: 'Kaizens em acompanhamento operacional', Valor: dashboardData.active_kaizens },
      { Métrica: 'Kaizens Sustentados', Valor: dashboardData.sustained_kaizens },
      { Métrica: 'Kaizens Regredidos', Valor: dashboardData.regressed_kaizens },
      { Métrica: 'Taxa de Sustentação (%)', Valor: metricText(dashboardData.avg_sustainability_rate) },
      { Métrica: 'Redução Total Lead Time (min)', Valor: metricText(dashboardData.total_lead_time_reduction) },
      { Métrica: 'Benefício anual ativo estimado (R$)', Valor: metricText(dashboardData.total_cost_savings) },
      { Métrica: 'FTEs Liberados', Valor: metricText(dashboardData.total_fte_freed) },
      { Métrica: 'Alertas Críticos', Valor: dashboardData.critical_alerts_count },
    ];

    const summarySheet = utils.json_to_sheet(summaryData);
    summarySheet['!cols'] = [{ wch: 40 }, { wch: 20 }];

    // ========================================================================
    // SHEET 2: Kaizens Detalhados
    // ========================================================================
    const kaizensData = kaizens.map((k) => {
      const reductionMinutes = measuredLeadReduction(k);
      const reduction = reductionMinutes == null || k.baseline_lead_time == null
        ? null
        : (reductionMinutes / k.baseline_lead_time) * 100;

      return {
        ID: k.id.substring(0, 8),
        Título: k.title,
        Tipo: k.type.replace(/_/g, ' '),
        Status: k.status,
        Responsável: k.responsible,
        'Data Planejada': new Date(k.planned_date).toLocaleDateString('pt-BR'),
        'Data Alvo': new Date(k.target_date).toLocaleDateString('pt-BR'),
        'Baseline Lead Time (min)': k.baseline_lead_time,
        'Target Lead Time (min)': k.target_lead_time,
        'Lead Time Atual (min)': k.current_lead_time ?? '',
        'Redução (%)': reduction == null ? '' : reduction.toFixed(2),
        'Baseline FTR (%)': k.baseline_ftr,
        'Target FTR (%)': k.target_ftr,
        'FTR Atual (%)': k.current_ftr ?? '',
        'Confiança': k.confidence_level,
      };
    });

    const kaizensSheet = utils.json_to_sheet(kaizensData);
    kaizensSheet['!cols'] = [
      { wch: 10 }, { wch: 30 }, { wch: 20 }, { wch: 15 }, { wch: 20 },
      { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
      { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 10 }
    ];

    // ========================================================================
    // SHEET 3: Análise por Tipo de Kaizen
    // ========================================================================
    const typeGroups = kaizens.reduce((acc, k) => {
      const type = k.type;
      if (!acc[type]) {
        acc[type] = { count: 0, reduction: 0, sustained: 0 };
      }
      acc[type].count++;
      const measured = measuredLeadReduction(k);
      if (measured != null) acc[type].reduction += measured;
      if (k.status === 'sustentado') acc[type].sustained++;
      return acc;
    }, {} as Record<string, { count: number; reduction: number; sustained: number }>);

    const typeAnalysisData = Object.entries(typeGroups).map(([type, data]) => ({
      Tipo: type.replace(/_/g, ' '),
      'Total de Kaizens': data.count,
      'Redução Total (min)': data.reduction.toFixed(2),
      'Kaizens Sustentados': data.sustained,
      'Taxa de Sustentação (%)': ((data.sustained / data.count) * 100).toFixed(2),
    }));

    const typeAnalysisSheet = utils.json_to_sheet(typeAnalysisData);
    typeAnalysisSheet['!cols'] = [{ wch: 25 }, { wch: 18 }, { wch: 20 }, { wch: 20 }, { wch: 22 }];

    // ========================================================================
    // SHEET 4: Análise por Status
    // ========================================================================
    const statusGroups = kaizens.reduce((acc, k) => {
      const status = k.status;
      if (!acc[status]) acc[status] = 0;
      acc[status]++;
      return acc;
    }, {} as Record<string, number>);

    const statusAnalysisData = Object.entries(statusGroups).map(([status, count]) => ({
      Status: status,
      'Quantidade': count,
      'Percentual (%)': ((count / kaizens.length) * 100).toFixed(2),
    }));

    const statusAnalysisSheet = utils.json_to_sheet(statusAnalysisData);
    statusAnalysisSheet['!cols'] = [{ wch: 20 }, { wch: 15 }, { wch: 18 }];

    // ========================================================================
    // WORKBOOK: Adicionar todas as sheets
    // ========================================================================
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, summarySheet, 'Dashboard');
    utils.book_append_sheet(workbook, kaizensSheet, 'Kaizens');
    utils.book_append_sheet(workbook, typeAnalysisSheet, 'Análise por Tipo');
    utils.book_append_sheet(workbook, statusAnalysisSheet, 'Análise por Status');

    // ========================================================================
    // SALVAR ARQUIVO
    // ========================================================================
    const sanitizedName = organizationName.replace(/[^a-z0-9]/gi, '_');
    const fileName = `GainsMonitor_${sanitizedName}_${new Date().toISOString().split('T')[0]}.xlsx`;
    writeFile(workbook, fileName);

    console.log(`✅ Relatório Excel exportado: ${fileName}`);
  } catch (error) {
    console.error('Erro ao exportar Excel:', error);
    throw new Error('Falha ao exportar relatório Excel. Tente novamente.');
  }
}

/**
 * 📊 Exporta relatório detalhado de um Kaizen com histórico de snapshots
 */
export async function exportKaizenDetailedReport(
  kaizen: KaizenRecord,
  snapshots: MetricsSnapshot[]
): Promise<void> {
  try {
    const XLSX = await import('xlsx');
    const { utils, writeFile } = XLSX;

    // ========================================================================
    // SHEET 1: Informações do Kaizen
    // ========================================================================
    const kaizenInfo = [
      { Campo: 'ID', Valor: kaizen.id.substring(0, 8) },
      { Campo: 'Título', Valor: kaizen.title },
      { Campo: 'Descrição', Valor: kaizen.description || 'N/A' },
      { Campo: 'Tipo', Valor: kaizen.type.replace(/_/g, ' ') },
      { Campo: 'Status', Valor: kaizen.status },
      { Campo: 'Responsável', Valor: kaizen.responsible },
      { Campo: 'Equipe', Valor: kaizen.team_members.join(', ') || 'N/A' },
      { Campo: '', Valor: '' },
      { Campo: 'Data Planejada', Valor: new Date(kaizen.planned_date).toLocaleDateString('pt-BR') },
      { Campo: 'Data de Implementação', Valor: kaizen.implementation_date ? new Date(kaizen.implementation_date).toLocaleDateString('pt-BR') : 'Pendente' },
      { Campo: 'Data Alvo', Valor: new Date(kaizen.target_date).toLocaleDateString('pt-BR') },
      { Campo: '', Valor: '' },
      { Campo: 'Baseline Lead Time (min)', Valor: kaizen.baseline_lead_time },
      { Campo: 'Target Lead Time (min)', Valor: kaizen.target_lead_time },
      { Campo: 'Current Lead Time (min)', Valor: kaizen.current_lead_time ?? 'Não informado' },
      { Campo: 'Redução Planejada (%)', Valor: kaizen.baseline_lead_time != null && kaizen.target_lead_time != null && kaizen.baseline_lead_time > 0
        ? ((1 - kaizen.target_lead_time / kaizen.baseline_lead_time) * 100).toFixed(2)
        : 'Não informado' },
    ];

    const kaizenInfoSheet = utils.json_to_sheet(kaizenInfo);
    kaizenInfoSheet['!cols'] = [{ wch: 30 }, { wch: 40 }];

    // ========================================================================
    // SHEET 2: Histórico de Snapshots
    // ========================================================================
    const snapshotsData = snapshots.map((s) => ({
      'Data Medição': new Date(s.measured_at).toLocaleDateString('pt-BR'),
      'Lead Time (min)': s.lead_time,
      'Cycle Time (min)': s.cycle_time,
      'FTR (%)': s.ftr,
      'Throughput': s.throughput,
      'Taxa Defeitos (%)': s.defect_rate,
      'Taxa Retrabalho (%)': s.rework_rate,
      'Tamanho Amostra': s.sample_size,
      'Observações': s.notes || '',
    }));

    const snapshotsSheet = utils.json_to_sheet(snapshotsData);
    snapshotsSheet['!cols'] = [
      { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 10 },
      { wch: 12 }, { wch: 15 }, { wch: 18 }, { wch: 15 }, { wch: 40 }
    ];

    // ========================================================================
    // WORKBOOK
    // ========================================================================
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, kaizenInfoSheet, 'Informações');
    utils.book_append_sheet(workbook, snapshotsSheet, 'Histórico de Medições');

    // ========================================================================
    // SALVAR ARQUIVO
    // ========================================================================
    const sanitizedTitle = kaizen.title.replace(/[^a-z0-9]/gi, '_').substring(0, 30);
    const fileName = `Kaizen_${sanitizedTitle}_${new Date().toISOString().split('T')[0]}.xlsx`;
    writeFile(workbook, fileName);

    console.log(`✅ Relatório detalhado exportado: ${fileName}`);
  } catch (error) {
    console.error('Erro ao exportar relatório detalhado:', error);
    throw new Error('Falha ao exportar relatório. Tente novamente.');
  }
}

/**
 * 📄 Exporta relatório executivo em PDF (simplificado)
 */
export async function exportGainsToPDF(
  kaizens: KaizenRecord[],
  dashboardData: GainsDashboardData,
  organizationName: string = 'Organização'
): Promise<void> {
  try {
    const jsPDF = (await import('jspdf')).default;
    const doc = new jsPDF();

    let yPos = 20;

    // Título
    doc.setFontSize(20);
    doc.text('Monitor de Sustentação de Ganhos', 20, yPos);
    yPos += 10;

    doc.setFontSize(12);
    doc.text(`Organização: ${organizationName}`, 20, yPos);
    yPos += 8;
    doc.text(`Data: ${new Date().toLocaleDateString('pt-BR')}`, 20, yPos);
    yPos += 15;

    // Dashboard Summary
    doc.setFontSize(16);
    doc.text('Resumo do Dashboard', 20, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.text(`Total de Kaizens: ${dashboardData.total_kaizens}`, 25, yPos);
    yPos += 6;
    doc.text(`Kaizens em acompanhamento operacional: ${dashboardData.active_kaizens}`, 25, yPos);
    yPos += 6;
    doc.text(`Kaizens Sustentados: ${dashboardData.sustained_kaizens}`, 25, yPos);
    yPos += 6;
    doc.text(`Taxa de Sustentação: ${metricText(dashboardData.avg_sustainability_rate)}%`, 25, yPos);
    yPos += 6;
    doc.text(`Redução Total Lead Time: ${metricText(dashboardData.total_lead_time_reduction)} min`, 25, yPos);
    yPos += 6;
    doc.text(`Benefício anual estimado: R$ ${metricText(dashboardData.total_cost_savings)}`, 25, yPos);
    yPos += 15;

    // Top Kaizens
    doc.setFontSize(16);
    doc.text('Top 5 Kaizens', 20, yPos);
    yPos += 10;

    const topKaizens = [...kaizens]
      .sort((a, b) => {
        const reductionA = measuredLeadReduction(a) ?? Number.NEGATIVE_INFINITY;
        const reductionB = measuredLeadReduction(b) ?? Number.NEGATIVE_INFINITY;
        return reductionB - reductionA;
      })
      .slice(0, 5);

    doc.setFontSize(10);
    topKaizens.forEach((k, idx) => {
      const reduction = measuredLeadReduction(k);
      doc.text(`${idx + 1}. ${k.title} - Redução: ${reduction == null ? 'não informada' : `${reduction.toFixed(1)} min`} (${k.status})`, 25, yPos);
      yPos += 6;
    });

    // Salvar PDF
    const sanitizedName = organizationName.replace(/[^a-z0-9]/gi, '_');
    const fileName = `GainsMonitor_${sanitizedName}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    console.log(`✅ Relatório PDF exportado: ${fileName}`);
  } catch (error) {
    console.error('Erro ao exportar PDF:', error);
    throw new Error('Falha ao exportar PDF. Tente novamente.');
  }
}

export const GainsExportService = {
  exportGainsToExcel,
  exportKaizenDetailedReport,
  exportGainsToPDF,
};

export default GainsExportService;
